/**
 * Prompt Optimizer page - PAIQ Analysis and Optimization
 * Workflow 8.1 from CLAUDE.md
 */
import { useEffect, useState } from 'react';
import {
  Breadcrumbs,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Link,
  LinearProgress,
  Paper,
  TextField,
  Typography,
  Chip,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SaveIcon from '@mui/icons-material/Save';
import PublicIcon from '@mui/icons-material/Public';

import { paiqApi, promptsApi } from '../../services/api';
import { useLocation, useNavigate } from 'react-router-dom';
import PublishToLibraryDialog from '../../components/PublishToLibraryDialog';
import {
  useDraftAutosave,
  loadDraft,
  clearDraft,
  formatDraftAge,
  type StoredDraft,
} from '../../hooks/useLocalDraft';

interface PAIQDimensionBar {
  key: string;
  label: string;
  value: number; // 0-100 (dimension score 0-5 scaled by 20)
}

interface PAIQScore {
  overall: number;
  dimensions: PAIQDimensionBar[];
}

// Canonical 7 PAIQ dimensions (must match shared/types/paiq.ts + scoring metaprompt).
const PAIQ_DIMENSION_LABELS: Array<[string, string]> = [
  ['firstPrinciplesDepth', 'First Principles Depth'],
  ['cognitiveConstraints', 'Cognitive Constraints'],
  ['crossDomainSynthesis', 'Cross-Domain Synthesis'],
  ['specificity', 'Specificity'],
  ['signalToNoise', 'Signal to Noise'],
  ['actionability', 'Actionability'],
  ['critiqueLoop', 'Critique Loop'],
];

interface PAIQSuggestion {
  category: string;
  issue: string;
  suggestion: string;
  priority: 'high' | 'medium' | 'low';
}

interface PAIQResult {
  score: PAIQScore;
  suggestions: PAIQSuggestion[];
  summary: string;
  strengths: string[];
  weaknesses: string[];
}

interface SmartPromptResult {
  smartPrompt: string;
  suggestedTitle: string;
  improvements: string[];
  explanation: string;
}

/** Shape of the autosaved draft for a new (unsaved) optimizer session. */
interface OptimizerDraft {
  originalPrompt: string;
  context: string;
  optimizedPrompt: string;
}

/** localStorage key for the new-prompt optimizer draft. */
const OPTIMIZER_DRAFT_KEY = 'optimizer-new';

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => (typeof item === 'string' ? item : ''))
    .filter((item) => item.trim().length > 0);
}

function normalizeSmartPromptResult(raw: unknown): SmartPromptResult {
  const source = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;

  const smartPrompt =
    (typeof source.smartPrompt === 'string' && source.smartPrompt) ||
    (typeof source.improvedPrompt === 'string' && source.improvedPrompt) ||
    '';

  return {
    smartPrompt,
    suggestedTitle: (typeof source.suggestedTitle === 'string' && source.suggestedTitle.trim()) || '',
    improvements: toStringArray(source.improvements),
    explanation: (typeof source.explanation === 'string' && source.explanation) || '',
  };
}

function normalizePaiqResult(raw: unknown): PAIQResult {
  const source = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const scoreNode =
    source.score && typeof source.score === 'object'
      ? (source.score as Record<string, unknown>)
      : null;

  const overallFromTop = typeof source.paiqScore === 'number' ? source.paiqScore : undefined;
  const overall =
    (scoreNode && typeof scoreNode.overall === 'number' && scoreNode.overall) ||
    overallFromTop ||
    0;

  const suggestionsRaw = Array.isArray(source.suggestions) ? source.suggestions : [];
  const suggestions: PAIQSuggestion[] = suggestionsRaw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const node = item as Record<string, unknown>;
      const priorityValue =
        node.priority === 'high' || node.priority === 'medium' || node.priority === 'low'
          ? node.priority
          : 'medium';

      return {
        category: (typeof node.category === 'string' && node.category) || 'Suggestion',
        issue: (typeof node.issue === 'string' && node.issue) || '',
        suggestion: (typeof node.suggestion === 'string' && node.suggestion) || '',
        priority: priorityValue,
      } as PAIQSuggestion;
    })
    .filter((item): item is PAIQSuggestion => Boolean(item));

  const dimensionsNode =
    source.dimensions && typeof source.dimensions === 'object'
      ? (source.dimensions as Record<string, unknown>)
      : {};

  const dimensions: PAIQDimensionBar[] = PAIQ_DIMENSION_LABELS.map(([key, label]) => {
    const dim = dimensionsNode[key];
    const rawScore =
      dim && typeof dim === 'object' && typeof (dim as Record<string, unknown>).score === 'number'
        ? ((dim as Record<string, unknown>).score as number)
        : 0;
    // Dimension scores are 1-5; scale to a 0-100 bar.
    return { key, label, value: Math.round(Math.max(0, Math.min(5, rawScore)) * 20) };
  });

  return {
    score: {
      overall,
      dimensions,
    },
    suggestions,
    summary: (typeof source.summary === 'string' && source.summary) || '',
    strengths: toStringArray(source.strengths),
    weaknesses: toStringArray(source.weaknesses),
  };
}

function ScoreBar({ label, value }: { label: string; value: number }) {
  const getColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight="bold">
          {value}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={value}
        color={getColor(value)}
        sx={{ height: 8, borderRadius: 1 }}
      />
    </Box>
  );
}

export default function OptimizerPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [context, setContext] = useState('');
  const [optimizedPrompt, setOptimizedPrompt] = useState('');
  const [paiqResult, setPaiqResult] = useState<PAIQResult | null>(null);
  const [smartResult, setSmartResult] = useState<SmartPromptResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [savingPaiq, setSavingPaiq] = useState(false);
  const [paiqSaved, setPaiqSaved] = useState(false);
  const [rawPaiqAnalysis, setRawPaiqAnalysis] = useState<Record<string, unknown> | null>(null);
  const [savedPromptId, setSavedPromptId] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [loadedPromptName, setLoadedPromptName] = useState('');
  const [loadedTags, setLoadedTags] = useState<string[]>([]);
  const [restorableDraft, setRestorableDraft] = useState<StoredDraft<OptimizerDraft> | null>(null);
  const params = new URLSearchParams(location.search);
  const promptId = params.get('promptId');
  const projectId = params.get('projectId');

  useEffect(() => {
    if (!promptId) {
      return;
    }

    void (async () => {
      try {
        const result = await promptsApi.get(promptId);
        const prompt = result?.data;
        if (!prompt) return;

        if (typeof prompt.promptName === 'string' && prompt.promptName.trim()) {
          setLoadedPromptName(prompt.promptName.trim());
        }
        if (Array.isArray(prompt.tags)) {
          setLoadedTags(prompt.tags.filter((t: unknown): t is string => typeof t === 'string'));
        }

        const originalContent =
          (typeof prompt.userPrompt === 'string' && prompt.userPrompt.trim()) ||
          '';

        if (originalContent) {
          setOriginalPrompt(originalContent);
        }

        // Load previously generated/optimized result into slot 2 (only if different from original)
        const generatedContent =
          (typeof prompt.generatedPrompt === 'string' && prompt.generatedPrompt.trim()) ||
          '';
        if (generatedContent && generatedContent !== originalContent) {
          setOptimizedPrompt(generatedContent);
        }

        if (typeof prompt.description === 'string' && prompt.description.trim()) {
          setContext(prompt.description);
        }
      } catch (err) {
        console.error('Failed to load prompt for optimization', err);
      }
    })();
  }, [location.search, promptId]);

  // For a fresh (unsaved) session, offer to restore a prior draft that would
  // otherwise have been lost to a mid-session logout, crash, or navigation.
  useEffect(() => {
    if (promptId) return;
    const draft = loadDraft<OptimizerDraft>(OPTIMIZER_DRAFT_KEY);
    const hasContent =
      !!draft?.data &&
      ((draft.data.originalPrompt ?? '').trim().length > 0 ||
        (draft.data.optimizedPrompt ?? '').trim().length > 0);
    if (draft && hasContent) {
      setRestorableDraft(draft);
    }
    // Only evaluate on initial mount / when switching into the new-prompt flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptId]);

  // Debounced autosave of the in-progress new prompt.
  useDraftAutosave<OptimizerDraft>(
    OPTIMIZER_DRAFT_KEY,
    { originalPrompt, context, optimizedPrompt },
    !promptId && (originalPrompt.trim().length > 0 || optimizedPrompt.trim().length > 0)
  );

  const handleRestoreDraft = () => {
    if (!restorableDraft) return;
    const d = restorableDraft.data;
    setOriginalPrompt(d.originalPrompt ?? '');
    setContext(d.context ?? '');
    setOptimizedPrompt(d.optimizedPrompt ?? '');
    setRestorableDraft(null);
  };

  const handleDismissDraft = () => {
    setRestorableDraft(null);
    clearDraft(OPTIMIZER_DRAFT_KEY);
  };

  const handleOptimize = async () => {
    if (!originalPrompt.trim()) return;
    
    setLoading(true);
    setError(null);
    setPaiqResult(null);
    setSmartResult(null);
    
    try {
      const result = await paiqApi.generateSmartPrompt(originalPrompt, context || undefined);
      console.log('[Optimizer] raw API result:', result);
      console.log('[Optimizer] result.data:', result?.data);
      const normalized = normalizeSmartPromptResult(result?.data);
      console.log('[Optimizer] normalized.smartPrompt (first 200 chars):', normalized.smartPrompt?.slice(0, 200));
      setSmartResult(normalized);
      setOptimizedPrompt(normalized.smartPrompt);
    } catch (err) {
      setError('Failed to optimize prompt. Please try again.');
      console.error('[Optimizer] handleOptimize error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    const promptToAnalyze = optimizedPrompt || originalPrompt;
    if (!promptToAnalyze.trim()) return;
    
    setAnalyzing(true);
    setError(null);
    
    try {
      const result = await paiqApi.calculateIQ(promptToAnalyze);
      const rawData =
        result?.data && typeof result.data === 'object'
          ? (result.data as Record<string, unknown>)
          : null;
      const normalized = normalizePaiqResult(result?.data);
      setRawPaiqAnalysis(rawData);
      setPaiqResult(normalized);

      // Auto-persist the analysis so it shows on the prompt without a separate save.
      const payload: Record<string, unknown> = {};
      if (typeof normalized?.score?.overall === 'number') payload.paiqScore = normalized.score.overall;
      if (rawData) payload.paiqAnalysis = rawData;
      if ('paiqScore' in payload || 'paiqAnalysis' in payload) {
        try {
          await persistPaiqPayload(payload);
          setPaiqSaved(true);
          setTimeout(() => setPaiqSaved(false), 3000);
        } catch (persistErr) {
          console.error('Auto-save of PAIQ analysis failed:', persistErr);
        }
      }
    } catch (err) {
      setError('Failed to analyze prompt. Please try again.');
      console.error(err);
    } finally {
      setAnalyzing(false);
    }
  };

  const buildPaiqPayload = (): Record<string, unknown> => {
    const paiqScore = paiqResult?.score?.overall;
    const paiqAnalysis =
      rawPaiqAnalysis && typeof rawPaiqAnalysis === 'object' ? rawPaiqAnalysis : undefined;
    return {
      ...(typeof paiqScore === 'number' ? { paiqScore } : {}),
      ...(paiqAnalysis ? { paiqAnalysis } : {}),
    };
  };

  // Persist PAIQ fields to the current prompt, updating an existing record or
  // creating one (and remembering its id so repeat saves don't duplicate).
  const persistPaiqPayload = async (paiqPayload: Record<string, unknown>) => {
    const targetId = promptId || savedPromptId;
    if (targetId) {
      await promptsApi.update(targetId, paiqPayload);
      return targetId;
    }
    const originalToSave = originalPrompt.trim();
    const optimizedToSave = (optimizedPrompt || originalPrompt).trim();
    const res = await promptsApi.create({
      name: smartResult?.suggestedTitle?.trim() || `Optimized Prompt ${new Date().toLocaleDateString()}`,
      content: originalToSave || optimizedToSave || 'Untitled prompt',
      generatedPrompt: optimizedToSave,
      description: context || smartResult?.explanation || 'Optimized via PAIQ',
      tags: ['optimized'],
      isPublic: false,
      ...paiqPayload,
      metadata: { originalPrompt },
    });
    const createdId = (res as { data?: { entityId?: string } })?.data?.entityId;
    if (createdId) setSavedPromptId(createdId);
    clearDraft(OPTIMIZER_DRAFT_KEY);
    return createdId;
  };

  const handleSave = async () => {
    const originalToSave = originalPrompt.trim();
    const optimizedToSave = (optimizedPrompt || originalPrompt).trim();
    if (!optimizedToSave) return;

    const targetId = promptId || savedPromptId;
    try {
      if (targetId) {
        // Only update generatedPrompt — never overwrite the original userPrompt
        await promptsApi.update(targetId, {
          generatedPrompt: optimizedToSave,
          description: context || smartResult?.explanation || 'Optimized via PAIQ',
          ...buildPaiqPayload(),
        });
      } else {
        const res = await promptsApi.create({
          name: smartResult?.suggestedTitle?.trim() || `Optimized Prompt ${new Date().toLocaleDateString()}`,
          content: originalToSave || optimizedToSave,
          generatedPrompt: optimizedToSave,
          description: context || smartResult?.explanation || 'Optimized via PAIQ',
          tags: ['optimized'],
          isPublic: false,
          ...buildPaiqPayload(),
          metadata: {
            originalPrompt: originalPrompt,
          },
        });
        const createdId = (res as { data?: { entityId?: string } })?.data?.entityId;
        if (createdId) setSavedPromptId(createdId);
      }
      clearDraft(OPTIMIZER_DRAFT_KEY);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Failed to save prompt. Please try again.');
      console.error(err);
    }
  };

  // Flush any in-progress edits to the saved prompt, then open the publish dialog.
  const handleOpenPublish = async () => {
    if (promptId || savedPromptId) {
      try {
        await handleSave();
      } catch (err) {
        console.error('Save before publish failed:', err);
      }
    }
    setPublishOpen(true);
  };

  const handleSavePaiqAnalysis = async () => {
    if (!paiqResult) return;
    const paiqPayload = buildPaiqPayload();
    if (!('paiqScore' in paiqPayload) && !('paiqAnalysis' in paiqPayload)) return;

    setSavingPaiq(true);
    setError(null);
    try {
      await persistPaiqPayload(paiqPayload);
      setPaiqSaved(true);
      setTimeout(() => setPaiqSaved(false), 3000);
    } catch (err) {
      setError('Failed to save PAIQ analysis. Please try again.');
      console.error(err);
    } finally {
      setSavingPaiq(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {projectId && (
        <Breadcrumbs sx={{ mb: 1.5 }} aria-label="breadcrumb">
          <Link
            component="button"
            type="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate('/projects')}
          >
            Projects
          </Link>
          <Link
            component="button"
            type="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/projects/${encodeURIComponent(projectId)}`)}
          >
            Edit Project
          </Link>
          <Typography color="text.primary">Prompt Optimizer</Typography>
        </Breadcrumbs>
      )}
      <Typography variant="h4" gutterBottom>
        Prompt Optimizer
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Improve your prompts with AI-powered optimization and quality analysis
      </Typography>

      {restorableDraft && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <>
              <Button color="inherit" size="small" onClick={handleRestoreDraft}>
                Restore
              </Button>
              <Button color="inherit" size="small" onClick={handleDismissDraft}>
                Dismiss
              </Button>
            </>
          }
        >
          Found an unsaved draft from {formatDraftAge(restorableDraft.savedAt)}. Restore it?
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {saved && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Prompt saved to your library!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Input Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Your Prompt
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={8}
              value={originalPrompt}
              onChange={(e) => setOriginalPrompt(e.target.value)}
              placeholder="Paste your prompt here..."
              variant="outlined"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={2}
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Additional context (optional)..."
              variant="outlined"
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                onClick={handleOptimize}
                disabled={loading || !originalPrompt.trim()}
              >
                {loading ? 'Optimizing...' : 'Optimize'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={analyzing ? <CircularProgress size={20} /> : <AnalyticsIcon />}
                onClick={handleAnalyze}
                disabled={analyzing || (!originalPrompt.trim() && !optimizedPrompt.trim())}
              >
                {analyzing ? 'Analyzing...' : 'Analyze'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Output Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Optimized Prompt
              </Typography>
              <Box>
                <Tooltip title="Analyze optimized prompt">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => void handleAnalyze()}
                      disabled={analyzing || !optimizedPrompt.trim()}
                    >
                      {analyzing ? <CircularProgress size={18} /> : <AnalyticsIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Copy">
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(optimizedPrompt)}
                    disabled={!optimizedPrompt}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Save to Library">
                  <IconButton
                    size="small"
                    onClick={handleSave}
                    disabled={!optimizedPrompt && !originalPrompt}
                    color="primary"
                  >
                    <SaveIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={(promptId || savedPromptId) ? 'Publish to public library' : 'Save the prompt first to publish'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleOpenPublish}
                      disabled={!(promptId || savedPromptId)}
                      color="success"
                    >
                      <PublicIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={8}
              value={optimizedPrompt}
              onChange={(e) => setOptimizedPrompt(e.target.value)}
              placeholder="Optimized prompt will appear here..."
              variant="outlined"
              sx={{ mb: 2 }}
            />

            {smartResult && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Improvements Made:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {toStringArray(smartResult.improvements).map((imp, i) => (
                    <Chip key={i} label={imp} size="small" color="success" variant="outlined" />
                  ))}
                </Box>
                {toStringArray(smartResult.improvements).length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    No structured improvements were returned for this optimization.
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* PAIQ Analysis Section */}
        {paiqResult && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                  PAIQ Analysis
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={savingPaiq ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSavePaiqAnalysis}
                  disabled={savingPaiq}
                >
                  {paiqSaved ? 'PAIQ Analysis Saved' : 'Save PAIQ Analysis'}
                </Button>
              </Box>
              
              <Grid container spacing={3}>
                {/* Score Card */}
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography variant="h2" color="primary">
                          {paiqResult.score.overall}
                          <Typography component="span" variant="h5" color="text.secondary">
                            {' / 35'}
                          </Typography>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Overall PAIQ Score
                        </Typography>
                      </Box>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      {paiqResult.score.dimensions.map((dim) => (
                        <ScoreBar key={dim.key} label={dim.label} value={dim.value} />
                      ))}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Summary & Strengths/Weaknesses */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Summary
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {paiqResult.summary}
                      </Typography>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="subtitle2" color="success.main" gutterBottom>
                        Strengths
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        {toStringArray(paiqResult.strengths).map((s, i) => (
                          <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                            • {s}
                          </Typography>
                        ))}
                        {toStringArray(paiqResult.strengths).length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            No structured strengths were returned.
                          </Typography>
                        )}
                      </Box>

                      <Typography variant="subtitle2" color="error.main" gutterBottom>
                        Areas for Improvement
                      </Typography>
                      <Box>
                        {toStringArray(paiqResult.weaknesses).map((w, i) => (
                          <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                            • {w}
                          </Typography>
                        ))}
                        {toStringArray(paiqResult.weaknesses).length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            No structured weaknesses were returned.
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Suggestions */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Suggestions
                      </Typography>
                      {(Array.isArray(paiqResult.suggestions) ? paiqResult.suggestions : []).map((suggestion, i) => (
                        <Box key={i} sx={{ mb: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {suggestion.category}
                            </Typography>
                            <Chip
                              label={suggestion.priority}
                              size="small"
                              color={getPriorityColor(suggestion.priority)}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {suggestion.issue}
                          </Typography>
                          <Typography variant="body2" color="success.main">
                            → {suggestion.suggestion}
                          </Typography>
                        </Box>
                      ))}
                      {(Array.isArray(paiqResult.suggestions) ? paiqResult.suggestions : []).length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No structured suggestions were returned.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>

      {(promptId || savedPromptId) && (
        <PublishToLibraryDialog
          open={publishOpen}
          onClose={() => setPublishOpen(false)}
          type="prompt"
          sourceId={(promptId || savedPromptId) as string}
          defaultTitle={loadedPromptName || smartResult?.suggestedTitle?.trim() || 'Optimized Prompt'}
          defaultDescription={context || smartResult?.explanation || ''}
          defaultTags={loadedTags.length ? loadedTags : ['optimized']}
          showPaiqOption={!!paiqResult}
        />
      )}
    </Box>
  );
}
